import React, { use, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Snackbar } from '@mui/material'
import type { TFunction } from 'i18next'
import { readAndCompressImage } from 'browser-image-resizer'
import { type OpenAPIResponseData, UserAPI } from '../api/api-switcher'
import { type ApiResult, toApiResultAsync } from '../api/api-error'
import { meKeys, settingKeys, searchKeys, invalidateMeQueries, invalidatePlanStorageBreakdownQueries } from '../api/query-keys'
import { auth_listener, useAuthStatus } from '../api/auth-listener'
import { imageFileToWidthHeight } from './image-uploader/ImageUploader'
import { markPhotoUploadHistory } from './image-uploader/first-upload-tracking'
import useLocalStorage from './useLocalStorage'
import InfoDialog from '../components/InfoDialog'
import { SeamlessSnackbarContext } from '../contexts/SeamlessSnackbarContext'

export type Me = OpenAPIResponseData<ReturnType<typeof UserAPI.getMe>>
export type UpdateMe = Pick<Me, 'user_name' | 'description' | 'twitter_id' | 'discord_id'>

// queryFn は正規化 union を返し throw しない（docs/design/async-react.md「エラーの扱い」）。
const meQueryFn = ({signal}: {signal: AbortSignal}) => toApiResultAsync(() => UserAPI.getMe(signal))

/**
 * Me（getMe）をキャッシュ購読する薄いフック。
 *
 * suspense ではなく `useQuery` を使う理由: 消費者は `me: Me | undefined` の
 * ソフトなローディング/エラー UX（取得中は undefined のまま描画、失敗は snackbar + undefined）を
 * 前提にしており、`enabled` ゲート（未認証で取得しない）も要る。取得の driver は `MeSideEffects`
 * のみで、ここは `enabled: false` にしてキャッシュ購読だけを行う（NotificationBell と同型の
 * app-wide soft read）。
 */
export const useMe = (): Me | undefined => useMeResult()?.data

/** useMe と同じ購読で、取得失敗（ApiResult の error）も見たい画面向け。未取得の間は undefined */
export const useMeResult = (): ApiResult<Me> | undefined => {
  const {data} = useQuery({
    queryKey: meKeys.me,
    queryFn: meQueryFn,
    enabled: false,
  })
  return data
}

/** 旧 MeContext.updateCache 相当。TQ キャッシュを in-place マージして更新後の Me を返す。 */
export const useUpdateMeCache = () => {
  const queryClient = useQueryClient()
  return (partial: Partial<Me>): Me | undefined => {
    const prev = queryClient.getQueryData<ApiResult<Me>>(meKeys.me)
    if (!prev?.data) return undefined
    const updated = {...prev.data, ...partial}
    queryClient.setQueryData<ApiResult<Me>>(meKeys.me, {...prev, data: updated, error: undefined})
    return updated
  }
}

/** 旧 MeContext.sync 相当。signal 引数は互換のため受けるが未使用（取得は TQ が管理）。 */
export const useSyncMe = () => {
  const queryClient = useQueryClient()
  return async (_signal?: AbortSignal): Promise<void> => {
    void _signal
    await invalidateMeQueries(queryClient)
  }
}

/** 旧 MeContext.update 相当。アイコンアップロード + プロフィール更新を行い成功時にキャッシュを差し替える。 */
export const useUpdateMe = () => {
  const queryClient = useQueryClient()
  const me = useMe()
  const {t} = useTranslation()
  const show_snackbar = use(SeamlessSnackbarContext)

  return async (new_me: UpdateMe, icon?: File): Promise<boolean> => {
    if (auth_listener.state !== 'authenticated') return false
    if (icon) {
      const {width, height} = await imageFileToWidthHeight(icon)
        .catch((e: Event) => {
          console.error('onerror event: ', e)
          return {width: 0, height: 0}
        })
      const last_modified = icon.lastModified
      if (width === 0 || height === 0) {
        show_snackbar(t('upload.iligal_data_error'))
        return false
      }
      const {error} = await UserAPI.uploadMeIcon(icon, width, height, last_modified)
      if (error) {
        const error_msg =
          error.code === 1401 ? t('upload.storage_over_error') :
          error.code === 1402 ? t('upload.file_size_upper_error') :
          error.code === 1403 ? t('upload.file_size_lower_error') :
          error.code === 1404 ? t('upload.file_format_error') :
          error.code === 1405 ? t('upload.file_break_error') :
                                t('upload.upload_failed')
        console.error('uploadMeIcon error: ', error)
        show_snackbar(error_msg)
        return false
      }
    }
    return await UserAPI.updateMe(new_me).then(async ({data, error}) => {
      let revoke_url = ''
      if (error || !data) {
        console.error('updateMe error: ', error)
        show_snackbar(t('error.update'))
        return false
      }
      else {
        if (icon) {
          if (me?.user_icon.startsWith('blob:')) revoke_url = me.user_icon
          const resize_config = {
            quality: 0.9,
            maxWidth: 240,
            maxHeight: 240,
            mimeType: 'image/jpeg',
          } as const
          const resized = await readAndCompressImage(icon, resize_config)
          data.user_icon = URL.createObjectURL(resized)
        }
        const prev = queryClient.getQueryData<ApiResult<Me>>(meKeys.me)
        queryClient.setQueryData<ApiResult<Me>>(meKeys.me, {
          data,
          error: undefined,
          response: prev?.response ?? new Response(),
        })
        queryClient.invalidateQueries({queryKey: searchKeys.allUsers})
        queryClient.invalidateQueries({queryKey: searchKeys.allSuggestions})
        URL.revokeObjectURL(revoke_url)
        return true
      }
    })
  }
}

/**
 * 戻り値は正しく値が返ってきた（≒エラーじゃない）かどうか
 */
const pushCampaignCode = (
  codes: string[],
  options: {
    t: TFunction,
    showSuccessDialog: (msg: string) => void
  }
): Promise<'upgrade' | 'no_change' | 'error'> =>
  UserAPI.sendCampaignCodes(codes).then(({data, error}) => {
    if (!error && data) {
      // 成功したキャンペーンがある場合はメッセージを表示
      const successful_campaigns = data.filter(result => result.campaign_id)
      if (successful_campaigns.length > 0) {
        // キャンペーンIDに基づいてメッセージを生成
        const campaign_messages = successful_campaigns.map(campaign =>
          campaign.campaign_id === 'vket2025s' ? options.t('campaign.vket2025summer_applied') :
          options.t('campaign.applied')
        )
        // 複数のキャンペーンがある場合は改行で結合
        options.showSuccessDialog(campaign_messages.join('\n'))
        return 'upgrade'
      }
      return 'no_change'
    }
    return 'error'
  })

/**
 * Me に紐づく app レベルの副作用シンク。Context 値は持たず children も wrap しない。
 * getMe 取得の唯一の driver（`useMe` は enabled:false の購読のみ）で、取得エラー snackbar /
 * campaign コード適用 + Dialog / ログイン・ログアウト時のキャッシュ破棄を担う。
 */
export const MeSideEffects: React.FC = () => {
  const is_auth = useAuthStatus() === 'authenticated'
  const queryClient = useQueryClient()
  const {t} = useTranslation()
  const [snackbar_msg, setSnackbarMsg] = useState('')
  const [campaign_dialog_msg, setCampaignDialogMsg] = useState('')
  const [campaign_codes, setCampaignCodes] = useLocalStorage<string[]>('campaign_codes', [])

  const {data: me_result} = useQuery({
    queryKey: meKeys.me,
    queryFn: meQueryFn,
    enabled: is_auth,
    refetchInterval: 60 * 60 * 1000,
  })

  // 取得エラーはソフトに snackbar（me は undefined のまま。値エラーで throw しない）
  useEffect(() => {
    if (me_result?.error) setSnackbarMsg(t('error.fetch_me'))
  }, [me_result, t])

  // 既に写真を持っている利用者は first_photo_upload_browser の対象外なので、
  // me が取れた時点で計測済みの印だけ付けておく（localStorage への冪等な書き込み）。
  useEffect(() => {
    const uploaded_count = me_result?.data?.storage_used_count_files
    if (uploaded_count !== undefined) markPhotoUploadHistory(uploaded_count)
  }, [me_result])

  // ログアウトでユーザー所有 cache を破棄（enabled:is_auth と併せて未認証で me undefined）
  useEffect(() => {
    if (!is_auth) {
      queryClient.removeQueries({queryKey: meKeys.me})
      queryClient.removeQueries({queryKey: settingKeys.notification})
    }
  }, [is_auth, queryClient])

  // ゲストのまま取得したキャッシュをログイン後に使わない（共有ページからログインして戻ったときなど）。
  // 画面にないものは捨てて次の表示で取り直し、表示中のものは取り直す。
  // 起動時の判定（configuring → authenticated）は API が認証の確定を待つので対象外
  useEffect(() => {
    let previous = auth_listener.state
    return auth_listener.listen(state => {
      if (previous === 'unauthenticated' && state === 'authenticated') {
        queryClient.removeQueries({type: 'inactive'})
        void queryClient.invalidateQueries()
      }
      previous = state
    })
  }, [queryClient])

  // キャンペーンコードがある場合は送信
  useEffect(() => {
    if (!is_auth || campaign_codes.length === 0) return
    pushCampaignCode(campaign_codes, {
      t,
      showSuccessDialog: setCampaignDialogMsg
    }).then(result => {
      // error がある場合（リクエスト自体のエラー）はコードを残す
      if (result === 'error') return
      setCampaignCodes([])
      if (result === 'upgrade') {
        // キャンペーンが適応された場合、ちょっと待ってから me を更新
        new Promise(r => setTimeout(r, 1000))
          .then(() => {
            invalidateMeQueries(queryClient)
            invalidatePlanStorageBreakdownQueries(queryClient)
          })
      }
    })

  }, [is_auth])

  return <>
    <Snackbar
      open={Boolean(snackbar_msg)}
      autoHideDuration={2000}
      onClose={() => { setSnackbarMsg('') }}
      message={snackbar_msg}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
    <InfoDialog
      open={Boolean(campaign_dialog_msg)}
      onClose={() => { setCampaignDialogMsg('') }}
      message={campaign_dialog_msg}
      ok_text={t('general.close')}
    />
  </>
}
